import {
  Component,
  ContentChild,
  ContentChildren,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef
} from '@angular/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { I18NService } from '@ebw-monorepo/i18n-translate';
import { EbwModalService } from '@ebw-monorepo/modal';
import { safeJsonParse, safeJsonStringify, safeJsonClone } from '@ebw-monorepo/microservices-helpers/utils/parser';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, take } from 'rxjs/operators';
import { CustomMatPaginatorIntl } from '../custom-mat-paginator-intl';
import { ListServiceService } from '../services/list-service.service';

type apiResponseModel = {
  numRows: number;
  records: any[];
};

export interface StyleFields {
  font_bold: string;
  upper: string;
  control: string;
  true: string;
  false: string;
}

@Component({
  selector: 'ebw-api-list',
  templateUrl: './api-list.component.html',
  styleUrls: ['./api-list.component.scss'],
  providers: [{ provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl }]
})
export class ApiListComponent implements OnInit, OnChanges, OnDestroy {
  constructor(
    private listService: ListServiceService,
    private ebwModalService: EbwModalService,
    private i18NSrv: I18NService
  ) {}

  /**
   * Variable to control the loading status of the list.
   */
  @Input() public loading: boolean = false;

  AUTOMATIC_PAGINATION_ELEMENTS = 10;

  /**
   * Data input from the title of the list
   */
  @Input() title: string = '';

  /**
   * Url input from where the list extracts the data. It should be a complete url.
   *
   * Example: https://ebw_test.com/user/list
   *
   * From this url all the operations are performed by using query parameters. The list must follow specific guidelines
   * that are specified in the documentation: https://confluence.ebwdatacenter.it/display/SWA/LLD+-+Lista
   */
  @Input() url: string = '';

  /**
   * Variable that holds the url query string.
   * url + urlQueryString makes the complete url for fectching the list from the backend.
   */
  urlQueryString: string = '';
  /**
   * Data input from the parent component to display.
   *
   * This variable contains all the values that the parent sends to the component.
   */
  data: {}[] = [];

  /**
   * This is the list that is displayed. This list comes already filtered from the API.
   */
  listToDisplay: {}[] = [];

  /**This is the list AFTER filters has been applied */
  filteredData: {}[] = [];

  @Input() filter: {
    filterFields: string[]; //The properties of the filter
    filterPlaceholder: string;
  } = { filterFields: [], filterPlaceholder: 'Filter' };

  listFilter: string = '';

  private _listFilterChange$: Subject<string> = new Subject();

  private _listFilterChangeSubscription: Subscription = new Subscription();

  /**
   * Input filter values received from the parent component.
   *
   * Its fields and values are sent as GET parameters to the API
   */
  @Input() apiFilter: any;

  /**
   * List of fields. Must contain at least 1 field.
   * Structure => {name: string, type: string, property: string, condition: string}[]
   *
   * name is the name of the field (human readable). Mandatory.
   *
   * type is the type of field: string | date | datetime | number | button | custom. Mandatory.
   *
   * property is the actual field in the data object. Mandatory.
   *
   * condition is a field name in which the button visibility relies on. Optional.
   *
   * A custom field is one where custom HTML code can be sent via content projection (https://angular.io/guide/content-projection).
   *
   * To use the custom field put the custom html code inside the <ebw-list> tag and inside this
   * <ng-template let-i #customField></ng-template>. The variable i controls the index of the list
   * and the #customField property tells the list where is the code intended to go.
   */
  // @Input() fields: { name: string, type: string, property: string, headerStyle?: object, contentStyle?: object }[] = [];
  @Input() fields: {
    name: string;
    type: string;
    property: string;
    truncate?: boolean;
    headerStyle?: object;
    contentStyle?: object;
    edit?: { property: string; contentStyle?: object };
    style?: object;
    conditionContentStyle?: object;
    templateId?: string;
  }[] = [];
  //This are the fields that are actually being displayed, to save the user input fields
  displayFields: {
    name: string;
    type: string;
    truncate?: boolean;
    property: string;
    headerStyle?: object;
    contentStyle?: object;
    style?: object;
    conditionContentStyle?: object;
    templateId?: string;
    templateRef?: TemplateRef<any>;
    edit?: {
      property: string;
      contentStyle?: object;
    };
  }[] = [];

  /**
   * List of fields that are sortable.
   */
  @Input() sortableFields: String[] = [];

  /**
   * Flag to enable/disable the clickable rows.
   *
   * Disabled by default if no value is specified.
   */
  @Input() clickableRows: boolean = false;

  /**
   * Variable that holds the current field in which the list is sorted.
   */
  currentField: String = '';

  /**
   * Variable that holds the current direction of sorting from a field.
   *
   * Direction can be 1 (descending), -1 (ascending), or 0 (no order)
   */
  currentDirection: number = 0;

  /**
   * Pagination options for the list. This list is always paginated.
   */
  pages: number = 0;

  /**
   * Input variable to trigger a reload.
   * Increment the value of the reload by 1 to trigger the onChanges function that reloads the list.
   */
  @Input() reload: number = 0;

  @Input() pagination: {
    enable: boolean | null;
    topPagination?: boolean;
    bottomPagination?: boolean;
    elementsPerPage: number;
  } = {
    enable: true,
    topPagination: true,
    bottomPagination: true,
    elementsPerPage: this.AUTOMATIC_PAGINATION_ELEMENTS
  };

  /**
   * The total amount of elements of the list. It comes with the request.
   */
  totalElements: number = 0;

  /**
   * Variable that holds the current page number.
   *
   * Pages go from 1 to "pages".
   */
  currentPage: number = 0;

  /**
   * Index of the first element in the displayed list with respect with the data list.
   *
   * Used for pagination.
   */
  lowerIndex: number = 0;

  /**
   * Index of the last element in the displayed list with respect with the data list.
   *
   * Used for pagination.
   */
  upperIndex: number = 0;

  @Input() selectable: boolean = false;

  selectStatus: boolean[] = [];
  /**
   * Checkbox to select all the elements in the page.
   */
  selectAllCheckbox: boolean = false;

  /**
   * For reference check: https://stackoverflow.com/questions/35190188/can-an-ng-content-be-used-inside-of-an-ngfor/60725235#60725235
   *
   * This variable allows to insert multiple html content into the custom field.
   *
   * For now, the list only allows 1 custom field.
   */
  @ContentChild('customField', { static: false })
  customFieldTemplateRef: TemplateRef<any>;

  @ContentChildren(TemplateRef) templates: QueryList<TemplateRef<any>>;

  /**
   * Event emitter for a row click.
   *
   * Triggered when a row is clicked and the property clickableRows is true.
   */
  @Output() rowClicked = new EventEmitter<any>();
  @Output() mouseEnter = new EventEmitter<any>();
  @Output() mouseLeave = new EventEmitter<any>();

  /**
   * Event emitter for a change in a checkbox.
   *
   * Triggered when a single checkbox is changed in a checkbox field or when the checkbox to select all the rows is changed.
   */
  @Output() rowSelected = new EventEmitter<any>();

  @Output() selectedItemsAction = new EventEmitter<any>();

  @Output() dataFetched = new EventEmitter<any>();

  @Input() selectedItemsButtonText: string = '';

  displayedColumns: string[] = [];

  @Input() pageNumber: number;
  @Output() actualPage = new EventEmitter<any>();

  translations: any;

  //function to edit the list
  @Input() customFn: (args: any) => void;

  ngOnInit(): void {
    this.listFilter = '';

    this._listFilterChangeSubscription = this._listFilterChange$.pipe(debounceTime(200)).subscribe({
      next: () => {
        if (this.currentPage !== 0) {
          this.handlePageChange({ pageIndex: 0 });
        }

        this.filterQuery();
      }
    });

    this.initTranslations();

    var i = 0;
    this.fields?.forEach((data) => {
      if (!data.hasOwnProperty('style')) {
        this.fields[i]['style'] = {
          font_bold: '',
          upper: '',
          control: '',
          true: '',
          false: ''
        };
      }
      i++;
    });
    // this.initList();
  }

  ngOnDestroy(): void {
    if (this._listFilterChangeSubscription) {
      this._listFilterChangeSubscription.unsubscribe();
    }
  }
  /**
   * Method that builds the url and fetches the list from the API.
   */
  fetchList(initial: boolean, buildQueryParams: boolean = false) {
    // this.loading = true;

    //If it is the first time it is fetching the list, put some default parameters.
    if (initial) {
      if (+this.pageNumber) {
        this.lowerIndex = this.pageNumber * this.pagination.elementsPerPage;
        this.currentPage = this.pageNumber;
      } else {
        this.lowerIndex = 0;
        this.currentPage = 0;
      }

      this.upperIndex = this.pagination.elementsPerPage;
    }

    if (buildQueryParams) {
      //First build the query parameters of the request.
      this.buildQueryParameters();
    }
    //Now request the list from the API
    const fetchUrl = this.url + this.urlQueryString;

    this.listService
      .getList(fetchUrl)
      .pipe(take(1))
      .subscribe({
        next: (apiData: apiResponseModel) => {
          this.totalElements = apiData.numRows;
          this.data = apiData.records;

          this.pages = Math.ceil(this.totalElements / this.pagination.elementsPerPage);

          this.setUpPaginationVariables();

          this.displayFields = [];

          if (this.fields.length == 0) {
            //automatically detect fields
            let dataRow = this.data[0];
            for (let [key, value] of Object.entries(dataRow)) {
              this.displayFields.push({
                name: key,
                type: typeof value,
                property: key
              });
            }
          } else {
            //If the fields input is provided, use it.
            this.displayFields = safeJsonClone(this.fields);

            this.displayFields.map((_) => {
              if (_.templateId) {
                const template = this.templates.find((t) =>
                  (<any>t)._declarationTContainer?.localNames?.includes(_.templateId)
                );
                if (template) {
                  _.templateRef = template;
                }
              }
            });
          }

          //Add the custom field at the end if there is a content projection
          if (this.customFieldTemplateRef && !this.displayFields.find((_) => _.type === 'custom')) {
            this.displayFields.push({
              name: 'custom',
              type: 'custom',
              property: 'custom'
            });
          }

          if (this.selectable) {
            //add the checkbod column
            this.displayFields.unshift({
              name: '',
              type: 'checkbox',
              property: ''
            });

            //add the selected field to the list
            for (let element of this.data) {
              element['selected'] = false;
            }
          }

          this.displayedColumns = this.displayFields.map((element) => {
            return element.name;
          });

          // this.loading = false;

          //aggiungo la proprietà 'content-style' ad ogni item della lista
          //cerco il field corrispondente e setto il contentStyle se non di tipo condizionato altrimenti lo calcolo
          this._setItemContentStyle(this.data, this.displayFields);

          this.applyFilters();

          this.dataFetched.emit(this.listToDisplay);
        },
        error: (error) => {
          this.ebwModalService.openErrorModal({
            title: this.translations.error,
            content: this.translations.data_retrieving_error
          });
        }
      });
  }

  /**
   * Initializes the component by:
   *
   * - Calculating the variables related to pagination if the paginate property is true.
   *
   * - Builds the listToDisplay by calling goToPage(1) if the pagination is enabled or directly setting the incoming data into the listToDisplay variable.
   *
   * - Checks if all items are selected to modify the checkbox that selects/unselects all rows.
   */
  initList() {
    if (this.url) {
      //First list fetch
      this.fetchList(true, true);
    }
  }

  /**
   * Check if all the checkboxes in the list are selected.
   */
  checkIfAllItemsSelected() {
    let allChecked = true && this.filteredData.length > 0;
    this.filteredData.forEach((element: any) => {
      allChecked = allChecked && element.selected;
    });
    this.selectAllCheckbox = allChecked;
  }

  /**
   * Method to return an array of a range specified.
   *
   * This builds an array of the form [init, init+1, ... , end-1, end].
   * @param init starting number of the range.
   * @param end final number of the range.
   * @returns Array of the form [init, init+1, ... , end-1, end].
   */
  range(init: number, end: number) {
    let arr = [];
    for (let i = init; i <= end; i++) {
      arr.push(i);
    }
    return arr;
  }

  /**
   * Sets up the global
   */
  setUpPaginationVariables(): void {
    this.lowerIndex = this.pagination.elementsPerPage * this.currentPage;
    const upperLimit = this.lowerIndex + this.pagination.elementsPerPage;
    this.upperIndex = upperLimit < this.totalElements ? upperLimit : this.totalElements;
  }

  handlePageChange($event) {
    //Internal pagination element starts from 1
    //Material pagination starts from 0
    this.actualPage.emit($event.pageIndex);
    this.goToPage($event.pageIndex);
  }

  /**
   * Changes the page displayed on the list. This controls also all the conditions and constraints that the
   * list have.
   * @param page The page number. Pages start in 1.
   */
  goToPage(page: number) {
    if (page <= this.pages && page >= 0) {
      this.currentPage = page;
      this.setUpPaginationVariables();
    } else {
      this.currentPage = 0;
      this.lowerIndex = 0;
      this.upperIndex = 0;
      this.listToDisplay = [];
    }
    this.filterQuery();
  }

  orderColumn(property, direction) {
    this.currentDirection = direction;
    this.currentField = property;
    const orderColumnObj = { property: property, direction: direction };
    sessionStorage.setItem('orderColumn', safeJsonStringify(orderColumnObj) ?? '');
    this.filterQuery();
  }

  /**
   * Centralized function per filtering and ordering the list
   */
  applyFilters() {
    //Make a deep copy of the original list
    if (this.data) {
      this.filteredData = safeJsonClone(this.data);

      //Filters by name
      // let filterName = this.listFilter.toUpperCase();

      // this.filteredData = this.filteredData.filter((element) => {
      //   let matches = false;
      //   for (let i in this.filter.filterFields) {
      //     let filterElement = this.filter.filterFields[i];

      //     const propertyToFilter = this.resolveObjectProperty(element, filterElement);

      //     if (propertyToFilter) {
      //       const propertyToFilterString = propertyToFilter.toString();
      //       matches = matches || propertyToFilterString.toUpperCase().includes(filterName);
      //     }
      //   }
      //   return matches;
      // });

      if (this.currentDirection != 0) {
        if (this.currentDirection == 1) {
          //Ascending order
          this.filteredData.sort((element1, element2) => {
            let element1_value = this.resolveObjectProperty(element1, this.currentField);
            if (element1_value == null) element1_value = '';
            let element2_value = this.resolveObjectProperty(element2, this.currentField);
            if (element2_value == null) element2_value = '';

            if (element1_value == element2_value) return 0;
            if (element1_value > element2_value) return 1;
            if (element1_value < element2_value) return -1;
            return 0;
          });
        } else {
          //Descending order
          this.filteredData.sort((element1, element2) => {
            let element1_value = this.resolveObjectProperty(element1, this.currentField);
            if (element1_value == null) element1_value = '';
            let element2_value = this.resolveObjectProperty(element2, this.currentField);
            if (element2_value == null) element2_value = '';

            if (element1_value == element2_value) return 0;
            if (element1_value < element2_value) return 1;
            if (element1_value > element2_value) return -1;
            return 0;
          });
        }
      }

      this.listToDisplay = safeJsonClone(this.filteredData);
      if (this.customFn) {
        this.customFn(this.listToDisplay);
      }
    }
  }

  public listFilterInputChange(value) {
    this._listFilterChange$.next(value);
  }

  filterQuery() {
    this.buildQueryParameters();
    if (this.listFilter && this.filter.filterFields.length > 0) {
      this.urlQueryString += '&searchFilters=' + this.filter.filterFields.join(',');
      this.urlQueryString += '&searchTerm=' + this.listFilter;
    }

    this.fetchList(false);
  }

  setUpPagination() {
    //Set up pagination
    if (this.pagination.enable == null) {
      this.pagination.enable = this.filteredData.length > this.AUTOMATIC_PAGINATION_ELEMENTS ? true : false;
    }
    if (this.pagination.enable) {
      this.pages = Math.ceil(this.filteredData.length / this.pagination.elementsPerPage);
      this.currentPage = 0;
      this.lowerIndex = 0;
      this.upperIndex = this.pagination.elementsPerPage;

      // this.goToPage(1);
    } else {
      this.listToDisplay = this.filteredData;
    }
  }

  buildQueryParameters() {
    //This sets the first character of the query string. In case the list already has parameters adds & instead of ?.
    let queryString = this.url.includes('?') ? '&' : '?';

    //Pagination is always applied
    let limit = this.pagination.elementsPerPage; // this.upperIndex;
    let offset = this.lowerIndex;
    queryString += 'limit=' + limit.toString() + '&offset=' + offset.toString();

    //Apply filter
    if (this.apiFilter && Object.keys(this.apiFilter).length > 0) {
      Object.keys(this.apiFilter).forEach((key) => {
        queryString += '&' + key + '=' + this.apiFilter[key];
      });
    }

    const orderColumn = sessionStorage.getItem('orderColumn');
    if (orderColumn) {
      const orderColumnObj = safeJsonParse(orderColumn);
      if (orderColumnObj) {
        this.currentField = orderColumnObj.property;
        this.currentDirection = orderColumnObj.direction;
      }
    }
    //Apply sorting
    if (this.sortableFields.length > 0 && this.currentDirection != 0) {
      queryString += '&order_by=' + this.currentField + '&order=' + this.currentDirection.toString();
    }

    //Assing the built query string to the global query string.
    this.urlQueryString = queryString;
  }

  /**
   * Reload the list on changes done in the parent component.
   * @param changes SimpleChanges object with the changes.
   */
  ngOnChanges(changes: SimpleChanges): void {
    // OF-4252: evito le "doppie richieste" al cambio di pagina nelle liste del GL
    const keys = Object.keys(changes);
    if (keys.length == 1 && keys[0] == 'pageNumber') {
      delete changes['pageNumber'];
      console.log('pageNumber changed ONLY, ngOnChanges suppressed');
    }

    const atLeastOneValueChanged = Object.values(changes).some(
      (_) => {
        const current = safeJsonStringify(_.currentValue);
        return current === null || current != safeJsonStringify(_.previousValue);
      }
    );

    /*if (changes['apiFilter'])
    {
      // if we change filtering in any way, we should reset the pagination state
      // before reloading data
      this.pageNumber = 0;
      this.lowerIndex = 0;

    }*/

    if (atLeastOneValueChanged) {
      this.initList();
    }
  }

  /**
   * Triggers the rowClicked event when a row is clicked and the property clickableRows is true.
   *
   * This send the entire row element as the $event data parameter.
   * @param rowData Complete row data element.
   */
  rowClickedEmit(rowData: any): void {
    if (this.clickableRows) {
      this.rowClicked.emit(rowData);
    }
  }

  mouseEnterEmit(rowData: any): void {
    this.mouseEnter.emit(rowData);
  }

  mouseLeaveEmit(rowData: any): void {
    this.mouseLeave.emit(rowData);
  }

  emitSelectedItemsAction() {
    try {
      let selectedRows = this.filteredData.filter((element: any) => {
        return element.selected;
      });
      this.selectedItemsAction.emit(selectedRows);
    } catch (error) {
      //Use trycatch to avoid the global loading to block the application
      console.log(error);
    }
  }

  /**
   * Triggers the checkboxChangedEmitter event when a checkbox state is changed.
   *
   * This send the entire row element as the $event data parameter.
   * @param rowData
   */
  checkboxChanged(rowData: any): void {
    rowData.selected = !rowData.selected;
    this.checkIfAllItemsSelected();
    this.rowSelected.emit(rowData);
  }

  /**
   * Method to select or deselect all checkboxes in the list page. This also emits checkboxChangedEmitter.
   */
  checkAllItems() {
    let valueToSet = true; //Check them all by default
    if (this.selectAllCheckbox) {
      //If all values checked, unselect them
      valueToSet = false;
    }

    this.filteredData.forEach((element: any) => {
      element.selected = valueToSet;
    });

    this.selectAllCheckbox = valueToSet;
    this.rowSelected.emit();
  }

  /**
   * Returns the value for the specified property in item, even when the property is into another property.
   * For example if the item is {a: {b: "value"}}, and the property is "a.b", it returns the value "value".
   * @param item Item to extract the value from
   * @param property Property name. Can be nested. For example "a.b" can be specified to access the propery "a" and then its propery "b".
   * @returns The value of "property" in the "item".
   */
  resolveObjectProperty(item: any, property: String) {
    try {
      let propertySequence = property.split('.');
      let objectProperty = item;
      for (let prop in propertySequence) {
        objectProperty = objectProperty[propertySequence[prop]];
      }

      return objectProperty;
    } catch (error) {
      return '';
    }
  }

  private _setItemContentStyle(data: any[], fields: any[]) {
    if (!data) {
      return;
    }

    data.forEach((item) => {
      item['contentStyle'] = {};
      let style = {};

      fields.forEach((field) => {
        if (field.contentStyle) {
          style = safeJsonClone(field.contentStyle);
        }

        if (field.conditionContentStyle) {
          style = Object.assign({}, style, this._buildCssObjFromConditionStyle(field.conditionContentStyle, item));
        }
        item['contentStyle'][field.property] = style;
      });
    });
  }

  private _buildCssObjFromConditionStyle(styleObj, item) {
    let cssObj = {};

    styleObj.forEach((_) => {
      let val: string;

      if (_.keys && _.keys.length > 0) {
        val = _.value;
        _.keys.forEach((key, i) => {
          val = val.replaceAll(`{${i}}`, String(item[key] ?? ''));
        });
      } else {
        val = `${_.value}`;
      }

      cssObj[_.cssProperty] = val;
    });

    return cssObj;
  }

  initTranslations() {
    let keys = ['common.error', 'common.data_retrieving_error'];

    this.i18NSrv.getAsyncTranslateMultiParameters(keys).subscribe((data) => {
      this.translations = {
        error: data['common.error'],
        data_retrieving_error: data['common.data_retrieving_error']
      };
    });
  }
}
